import { api } from "_shared/_stores/api";
import FeeScheduleSchema, { FeeScheduleJSON } from "marketplace/teams/fee-schedules/_schemas/fee-schedule-schema";
import FeeScheduleCommand from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-command";
import UpdateFeeScheduleCommand from "marketplace/teams/fee-schedules/_api/commands/update-fee-schedule-command";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import z from "zod";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";

export const feeScheduleApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getFeeSchedulesForTeam: build.query<FeeScheduleJSON[], { teamId: string }>({
      query: ({ teamId }) => `marketplace/teams/${teamId}/fee-schedules`,
      rawResponseSchema: z.array(FeeScheduleSchema.FeeScheduleJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { teamId }) => [{ type: "FeeSchedule" as const, id: teamId }],
    }),
    // #endregion

    // #region Create
    createFeeSchedule: build.mutation<FeeScheduleJSON, { teamId: string; feeSchedule: FeeSchedule }>({
      query: ({ teamId, feeSchedule }) => ({
        url: `marketplace/teams/${teamId}/fee-schedules`,
        method: "POST",
        // `.payload`, not the command itself: a body RTK Query cannot recognize as JSON loses both
        // its serialization and its Content-Type. No header named here — this is not a patch, and
        // an unpatched body with no stated preference already gets `application/json`.
        body: new FeeScheduleCommand(feeSchedule).payload,
      }),
      rawResponseSchema: FeeScheduleSchema.FeeScheduleJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "FeeSchedule" as const, id: teamId }],
    }),
    // #endregion

    // #region Update
    updateFeeSchedule: build.mutation<
      FeeScheduleJSON,
      { teamId: string; scheduleId: string; originalFeeSchedule: FeeSchedule; updatedFeeSchedule: FeeSchedule }
    >({
      query: ({ teamId, scheduleId, originalFeeSchedule, updatedFeeSchedule }) => ({
        url: `marketplace/teams/${teamId}/fee-schedules/${scheduleId}`,
        method: "PATCH",
        body: new UpdateFeeScheduleCommand(originalFeeSchedule, updatedFeeSchedule).payload,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: FeeScheduleSchema.FeeScheduleJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "FeeSchedule" as const, id: teamId }],
    }),
    // #endregion

    // #region Delete
    deleteFeeSchedule: build.mutation<void, { teamId: string; scheduleId: string }>({
      query: ({ teamId, scheduleId }) => ({
        url: `marketplace/teams/${teamId}/fee-schedules/${scheduleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "FeeSchedule" as const, id: teamId }],
    }),
    // #endregion
  }),
});

export const {
  useGetFeeSchedulesForTeamQuery,
  useCreateFeeScheduleMutation,
  useUpdateFeeScheduleMutation,
  useDeleteFeeScheduleMutation,
} = feeScheduleApi;
